import { Box, Button, useToast } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { ApiMessageStack, TableV2 } from "components";
import React, { FC, useCallback, useMemo, useState } from "react";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { keyBy } from "lodash";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { TMRIRadarNegotiatorRelationExtendedData } from "types/mri-radar.type";
import { syncMRIRadarNegotiatorTableColumnDef } from "./syncMRIRadarNegotiatorTableColumnDef";
import { IntegrationConfiguratorLayout } from "../../IntegrationConfiguratorLayout";

const DEFAULT_PAGE_SIZE = 10;

const normalizeUsername = (value: string | null): string | null => {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
};

interface IProps {
  accountStore: UserAccountStore;
  isManuallyTriggered: boolean;
}

export const MRIRadarIntegrationNegotiatorsConfigurator: FC<IProps> = observer(
  ({ accountStore, isManuallyTriggered }) => {
    const toast = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newRelations, setNewRelations] = useState<
      Record<string, TMRIRadarNegotiatorRelationExtendedData>
    >({});

    const accountIntegrationMRIRadarNegotiatorsStore =
      accountStore.accountIntegrationsStore.accountIntegrationMRIRadarStore
        .accountIntegrationMRIRadarNegotiatorsStore;
    const {
      mriRadarNegotiatorsData,
    } = accountIntegrationMRIRadarNegotiatorsStore;

    const updateSyncPair = useCallback(
      (syncPair: TMRIRadarNegotiatorRelationExtendedData) => {
        setNewRelations(prev => ({
          ...prev,
          [syncPair.userAccountId]: syncPair,
        }));
      },
      [setNewRelations]
    );

    const columnsDef = useMemo(() => {
      return syncMRIRadarNegotiatorTableColumnDef(updateSyncPair);
    }, [updateSyncPair]);

    const dataSource = useMemo(() => {
      return mriRadarNegotiatorsData.map(syncData => {
        if (!!newRelations[syncData.userAccountId])
          return newRelations[syncData.userAccountId];

        return syncData;
      });
    }, [mriRadarNegotiatorsData, newRelations]);

    const changedRelations = useMemo(() => {
      const savedByUserAccountId = keyBy(
        mriRadarNegotiatorsData,
        data => data.userAccountId
      );

      return Object.values(newRelations)
        .map(relation => ({
          ...relation,
          mriNegotiatorEmail: normalizeUsername(relation.mriNegotiatorEmail),
        }))
        .filter(relation => {
          const saved = savedByUserAccountId[relation.userAccountId];
          return (
            relation.mriNegotiatorEmail !==
            normalizeUsername(saved?.mriNegotiatorEmail ?? null)
          );
        });
    }, [newRelations, mriRadarNegotiatorsData]);

    const submitSyncPairs = useCallback(async () => {
      if (changedRelations.length === 0) return;

      try {
        setIsSubmitting(true);
        await accountIntegrationMRIRadarNegotiatorsStore.updateAccountMRIRadarSyncedNegotiators(
          changedRelations
        );
        setNewRelations({});
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: (
            <ApiMessageStack
              messageStack={"MRI Radar negotiator mappings updated."}
            />
          ),
        });
      } catch (error) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={error.message} />,
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [changedRelations, accountIntegrationMRIRadarNegotiatorsStore, toast]);

    return (
      <IntegrationConfiguratorLayout disabled={!isManuallyTriggered}>
        <Box fontSize={"xl"}>Negotiator mappings</Box>
        <Box fontSize={"md"} color={"leadpro.400"}>
          Map each LeadPro user to their MRI login email — this is the filenote
          Owner in MRI, and it must be a valid MRI login email.
        </Box>
        <TableV2<TMRIRadarNegotiatorRelationExtendedData>
          globalFilterInputPlaceholder={"Search users by name"}
          dataSource={dataSource}
          columns={columnsDef}
          pageSize={DEFAULT_PAGE_SIZE}
        />
        <Box width={"100%"} textAlign={"right"}>
          <Button
            colorScheme={"blue"}
            onClick={submitSyncPairs}
            isLoading={isSubmitting}
            disabled={!isManuallyTriggered || changedRelations.length === 0}
          >
            Update
          </Button>
        </Box>
      </IntegrationConfiguratorLayout>
    );
  }
);
